import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import { plPL } from '@mui/material/locale';

// Freshpod brand colors, taken from freshpod.pl
const BRAND_ORANGE = '#FE5000';
const BRAND_GRAPHITE = '#3A4856';

const COLOR_MODE_STORAGE_KEY = 'store-web-app.color-mode';

export const ColorModePreferenceEnum = {
    LIGHT: { value: 'light', label: 'Jasny' },
    DARK: { value: 'dark', label: 'Ciemny' },
    SYSTEM: { value: 'system', label: 'Systemowy' }
};

// every color used by views comes from here, so both modes stay readable
const TOKENS = {
    light: {
        brand: {
            main: BRAND_ORANGE,
            light: '#FF7A3D',
            dark: '#D94400',
            // orange on white is below 4.5:1, text uses a darker step
            text: '#C23D00',
            lighter: '#FEEFE8'
        },
        slate: {
            50: '#f8fafc',
            100: '#f3f5f7',
            200: '#e2e8f0',
            300: '#cbd5e1',
            500: '#64748b',
            600: '#475569',
            800: '#1e293b'
        },
        text: { primary: '#1c2430', secondary: '#5f6b78' },
        background: { default: '#f3f5f7', paper: '#ffffff' },
        secondary: { main: BRAND_GRAPHITE, light: '#e7ebef' },
        // crimson, the plain red #dc2626 was too close to the brand orange to tell an error from a primary element
        error: { main: '#be123c' },
        warning: { main: '#eab308', contrastText: '#1c1917' },
        info: { main: '#0284c7' },
        success: { main: '#16a34a' },
        // a calm bar, the brand color is only an accent line, a full orange bar was tiring
        appBar: {
            background: '#ffffff',
            color: '#1c2430',
            borderBottom: `3px solid ${ BRAND_ORANGE }`
        }
    },
    dark: {
        brand: {
            main: '#FF6A1F',
            light: '#FF8A4C',
            dark: '#E55000',
            text: '#FF9A66',
            lighter: 'rgba(255, 106, 31, 0.16)'
        },
        slate: {
            50: '#1b2027',
            100: '#14181d',
            200: '#2a313a',
            300: '#3b4450',
            500: '#94a0ad',
            600: '#b4bec9',
            800: '#e5e9ee'
        },
        text: { primary: '#e8ecf1', secondary: '#9aa6b2' },
        background: { default: '#0f1215', paper: '#171b21' },
        secondary: { main: '#8fa3b8', light: 'rgba(143, 163, 184, 0.16)' },
        error: { main: '#d6336c' },
        warning: { main: '#facc15', contrastText: '#1c1917' },
        info: { main: '#38bdf8' },
        success: { main: '#4ade80' },
        appBar: {
            background: '#171b21',
            color: '#e8ecf1',
            borderBottom: '3px solid #FF6A1F'
        }
    }
};

export const createAppTheme = (mode) => {
    const tokens = TOKENS[mode];
    const { brand, slate } = tokens;

    return createTheme({
        shape: {
            borderRadius: 10
        },

        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            // Inter runs larger than Roboto at the same size, 13px keeps dense tables and forms readable without crowding
            fontSize: 13,
            h1: { fontSize: '1.7em', fontWeight: 700, letterSpacing: '-0.02em' },
            h2: { fontSize: '1.5em', fontWeight: 700 },
            h3: { fontSize: '1.4em', fontWeight: 700 },
            h4: { fontSize: '1.3em', fontWeight: 700 },
            h5: { fontSize: '1.2em', fontWeight: 700 },
            h6: { fontSize: '1.1em', fontWeight: 700 },
            button: { fontWeight: 600 }
        },

        palette: {
            mode,
            primary: {
                main: brand.main,
                light: brand.light,
                dark: brand.dark,
                text: brand.text,
                lighter: brand.lighter,
                contrastText: '#ffffff'
            },
            secondary: tokens.secondary,
            text: tokens.text,
            error: tokens.error,
            warning: tokens.warning,
            info: tokens.info,
            success: tokens.success,
            divider: slate[200],
            background: tokens.background,
            slate,
            // cell highlights of the pod sales distribution table
            promotion: {
                active: alpha(tokens.success.main, mode === 'dark' ? 0.22 : 0.16),
                planned: alpha(tokens.warning.main, mode === 'dark' ? 0.22 : 0.2),
                excluded: alpha(tokens.error.main, mode === 'dark' ? 0.25 : 0.18)
            }
        },

        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        background: tokens.appBar.background,
                        color: tokens.appBar.color,
                        borderBottom: tokens.appBar.borderBottom,
                        boxShadow: '0 1px 3px rgb(15 23 42 / 0.12)'
                    }
                }
            },

            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        borderRight: `1px solid ${ slate[200] }`
                    }
                }
            },

            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        margin: '2px 8px',
                        '&:hover': {
                            backgroundColor: slate[100]
                        },
                        '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: brand.lighter,
                            color: brand.text,
                            '& .MuiListItemIcon-root': {
                                color: brand.main
                            },
                            '& .MuiListItemText-primary': {
                                fontWeight: 600
                            }
                        }
                    }
                }
            },

            MuiListItemIcon: {
                styleOverrides: {
                    root: {
                        minWidth: 40,
                        color: slate[500]
                    }
                }
            },

            // menu entries and form fields use the same compact size as table content
            MuiListItemText: {
                styleOverrides: {
                    primary: ({ theme }) => ({
                        fontSize: theme.typography.body2.fontSize
                    })
                }
            },

            MuiInputBase: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: theme.typography.body2.fontSize
                    })
                }
            },

            MuiInputLabel: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: theme.typography.body2.fontSize
                    })
                }
            },

            MuiMenuItem: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: theme.typography.body2.fontSize
                    })
                }
            },

            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none'
                    },
                    outlined: {
                        borderColor: slate[200]
                    }
                }
            },

            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: 8
                    },
                    outlined: {
                        backgroundColor: tokens.background.paper
                    },
                    outlinedPrimary: {
                        color: brand.text,
                        borderColor: alpha(brand.main, 0.5)
                    },
                    textPrimary: {
                        color: brand.text
                    }
                }
            },

            // table actions use the text color, only edit (primary) and activation (success / error) are colored explicitly
            MuiIconButton: {
                variants: [
                    {
                        props: { color: 'default' },
                        style: {
                            color: tokens.text.primary
                        }
                    }
                ]
            },

            MuiLink: {
                styleOverrides: {
                    root: {
                        color: brand.text
                    }
                }
            },

            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        color: slate[600],
                        backgroundColor: tokens.background.paper,
                        '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: brand.lighter,
                            color: brand.text
                        }
                    }
                }
            },

            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        height: 3,
                        borderRadius: '3px 3px 0 0'
                    }
                }
            },

            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        '&.Mui-selected': {
                            color: brand.text
                        }
                    }
                }
            },

            MuiTableCell: {
                styleOverrides: {
                    head: {
                        color: slate[600],
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        backgroundColor: slate[50],
                        borderBottom: `1px solid ${ slate[200] }`
                    },
                    body: {
                        borderBottom: `1px solid ${ slate[100] }`
                    }
                }
            },

            MuiTableRow: {
                styleOverrides: {
                    root: {
                        textDecoration: 'none',
                        '&.MuiTableRow-hover:hover': {
                            backgroundColor: alpha(brand.main, 0.06)
                        }
                    }
                }
            },

            MuiChip: {
                styleOverrides: {
                    root: {
                        fontWeight: 500
                    },
                    outlined: {
                        borderColor: slate[300],
                        backgroundColor: tokens.background.paper
                    }
                }
            },

            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: tokens.background.paper
                    }
                }
            },

            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: '0.8rem',
                        backgroundColor: mode === 'dark' ? slate[300] : slate[800]
                    }
                }
            },

            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: 4
                    }
                }
            },

            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 14
                    }
                }
            }
        }
    }, plPL);
};

const ColorModeContext = createContext({
    mode: ColorModePreferenceEnum.LIGHT.value,
    preference: ColorModePreferenceEnum.SYSTEM.value,
    setPreference: () => {}
});

export const useColorMode = () => useContext(ColorModeContext);

const readPreference = () => {
    try {
        return localStorage.getItem(COLOR_MODE_STORAGE_KEY) || ColorModePreferenceEnum.SYSTEM.value;
    } catch (e) {
        return ColorModePreferenceEnum.SYSTEM.value;
    }
};

export const AppThemeProvider = ({ children }) => {
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
    const [preference, setPreference] = useState(readPreference);

    useEffect(() => {
        try {
            localStorage.setItem(COLOR_MODE_STORAGE_KEY, preference);
        } catch (e) {
            // the choice is a convenience, the app works without it
        }
    }, [preference]);

    const mode = preference === ColorModePreferenceEnum.SYSTEM.value
        ? (prefersDark ? ColorModePreferenceEnum.DARK.value : ColorModePreferenceEnum.LIGHT.value)
        : preference;

    const theme = useMemo(() => createAppTheme(mode), [mode]);
    const context = useMemo(() => ({ mode, preference, setPreference }), [mode, preference]);

    return (
        <ColorModeContext.Provider value={ context }>
            <ThemeProvider theme={ theme }>
                { children }
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};
